/**
 * PE - Pill Collection
 * ---------------------------------------------------------------------------
 * A row of tappable "pill" cards placed above the collection product grid.
 * Tapping a card swaps the collection in place (no full reload) and updates
 * the URL via the History API.
 *
 * Two pieces:
 *   - PillNavigator, ONE per page. Owns everything page-scoped: which
 *     collection is on screen, the Back-pill trail, Back/Forward handling,
 *     in-flight request cancellation and the section prefetch cache. A page
 *     can carry several pill rows (collection.summer-party has two); when each
 *     row kept its own copy of this state they drifted apart, and every
 *     Back/Forward was handled once per row.
 *   - <pill-collection>, one per row. Rendering, clicks, drag-to-scroll, the
 *     Back pill. It delegates every navigation to the navigator.
 *
 * THE RULE: an in-place switch must produce exactly what a real load of the
 * target URL would. The Section Rendering API (`?sections=<key>`) renders the
 * section key it is given — which is scoped to the CURRENT template — against
 * whatever URL it is asked for. For a target collection on a DIFFERENT
 * template that is a hybrid no real page load would ever produce: the current
 * template's settings (data source, shape) applied to the target's data. That
 * was the whole of the "pills vanish" / "circles turn into black capsules" /
 * "sections go missing after Back" family of bugs. So:
 *   - same template      -> switch in place, refreshing every pill row, the
 *                           collection header and the Custom Liquid sections;
 *   - different template -> normal navigation. Only a real load can add or
 *                           remove whole sections and boot the RCC app block.
 * The template is baked into the markup (data-template-suffix on the row,
 * data-pill-template on collection cards), and every response is verified
 * against it before it is painted — a mismatch falls back to a real load.
 *
 * Grids:
 *   vertex -> Nimstrata / Retail Cloud Connect app block. Collection switches
 *             go through the registered RCC store's setCollection(); filter
 *             cards let the app re-read the URL via a popstate.
 *   native -> the theme's own collection section, swapped from its fragment.
 */
(() => {
  // The section includes this script once per instance, so a template with two
  // pill rows evaluates it twice. Scoped in this IIFE so the second copy cannot
  // collide with the first's declarations, and bail before redefining.
  if (customElements.get('pill-collection')) return;

  /** sessionStorage key mirroring the Back-pill trail (see PillNavigator#trail). */
  const TRAIL_KEY = 'pePillCollectionTrail';

  /**
   * sessionStorage key for path -> collection identity. Back/Forward needs the
   * id of the collection a history entry points at, and the entry's own state
   * is not reliable: the landing entry starts as `null`, and the RCC app and
   * the theme's facets write their own state objects over ours.
   */
  const INDEX_KEY = 'pePillCollectionIndex';
  const INDEX_LIMIT = 50;

  /** Shopify's Section Rendering API serves at most 5 sections per request. */
  const SECTIONS_PER_REQUEST = 5;

  /** Hover prefetch: how long a pointer must rest on a card, cache size and TTL. */
  const HOVER_DELAY_MS = 100;
  const PREFETCH_LIMIT = 16;
  const PREFETCH_TTL_MS = 5 * 60 * 1000;

  /**
   * sessionStorage key carrying a pill-triggered real load's landing position
   * to the next page: where the clicked row sat in the viewport, so the new
   * page opens with its row in the same spot instead of jumping to the top.
   * Consumed once, ignored when older than ANCHOR_TTL_MS.
   */
  const ANCHOR_KEY = 'pePillCollectionAnchor';
  const ANCHOR_TTL_MS = 10 * 1000;

  /** White chevron for the Back pill. Coloured via CSS `currentColor`. */
  const BACK_ARROW_SVG =
    '<svg class="pe-pill-collection__back-arrow" viewBox="0 0 24 24" aria-hidden="true" ' +
    'focusable="false"><path d="M15 4 7 12l8 8" fill="none" stroke="currentColor" ' +
    'stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  /**
   * The `/collections/<handle>` part of a URL, INCLUDING any locale prefix
   * (`/fr/collections/…`, `/en-us/collections/…`); '' when it is not a
   * collection URL. The old `^/collections/` patterns never matched on the
   * French or US storefronts.
   */
  function collectionPath(url) {
    let pathname;
    try {
      pathname = new URL(url, window.location.origin).pathname;
    } catch (error) {
      return '';
    }
    const match = pathname.match(/^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/collections\/[^/]+/i);
    return match ? match[0] : '';
  }

  function samePath(a, b) {
    return !!a && !!b && a.toLowerCase() === b.toLowerCase();
  }

  /** Query string of a URL, without the leading `?`. */
  function queryOf(url) {
    return new URL(url, window.location.origin).search.replace(/^\?/, '');
  }

  /**
   * A template suffix read off markup or stored state. `null` means UNKNOWN
   * (attribute absent, e.g. a trail recorded before this code shipped) and is
   * resolved by verifying the response; '' is the default template.
   */
  function templateOf(value) {
    return value === undefined || value === null ? null : String(value);
  }

  /** Section key (what the sections API wants) from a `#shopify-section-…` id. */
  function sectionKey(el) {
    return el && el.id ? el.id.replace(/^shopify-section-/, '') : '';
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function readJSON(key) {
    try {
      return JSON.parse(sessionStorage.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  }

  function writeJSON(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Private mode / quota — history.state alone still covers the common path.
    }
  }

  /** history.state as a plain object we can safely merge into. */
  function currentState() {
    const state = window.history.state;
    return state && typeof state === 'object' ? state : {};
  }

  /**
   * The wrapper Shopify emits around the NATIVE collection section. Resolved
   * live so a prior swap doesn't leave us holding a stale node.
   */
  function nativeGrid() {
    const container = document.getElementById('Product-Grid-Container');
    return container ? container.closest('.shopify-section') : null;
  }

  /**
   * The Nimstrata / Retail Cloud Connect (Google Vertex AI Search) app-block
   * element. Its id looks like
   * `shopify-block-…__retail_cloud_connect_app_categories_block_…`.
   */
  function vertexGrid() {
    return document.querySelector('[id*="retail_cloud_connect"]');
  }

  /** The RCC store registered for the app block on this page. */
  function vertexSearch() {
    const registry = window.RCCSearchStores || {};
    const grid = vertexGrid();
    const storeKey = grid && grid.querySelector('[data-search-store]')?.dataset.searchStore;
    return (storeKey && registry[storeKey]) || Object.values(registry)[0] || null;
  }

  /** Prefer Vertex when present: if the app block is on the page, it is the product display. */
  function gridMode() {
    if (vertexGrid()) return 'vertex';
    if (nativeGrid()) return 'native';
    return 'none';
  }

  /**
   * The "Collection - Header" section wrapper. The theme editor generates
   * section keys with UNDERSCORES (`main_collection_header_KdL86y`), not the
   * hyphenated section TYPE — match both spellings.
   */
  function findCollectionHeader(root) {
    return root.querySelector(
      '.shopify-section[id*="main_collection_header"], .shopify-section[id*="main-collection-header"]'
    );
  }

  /**
   * Custom Liquid sections on the page. Collection-scoped by nature on a
   * collection page — the store renders the collection description through
   * one — so they re-render when the collection changes in place.
   */
  function customLiquidSections() {
    return Array.from(
      document.querySelectorAll('.shopify-section[id*="custom_liquid"], .shopify-section[id*="custom-liquid"]')
    );
  }

  /**
   * Ask Shopify for just the sections we need, as `{ key: Document }`. Batched
   * because the API caps a request at 5 sections. Keys the API returns null
   * for are simply absent from the result; the caller verifies what it needs.
   */
  async function fetchSections(url, keys, signal) {
    const chunks = [];
    for (let i = 0; i < keys.length; i += SECTIONS_PER_REQUEST) {
      chunks.push(keys.slice(i, i + SECTIONS_PER_REQUEST));
    }

    const payloads = await Promise.all(
      chunks.map(async (chunk) => {
        const target = new URL(url, window.location.origin);
        target.searchParams.set('sections', chunk.join(','));
        const response = await fetch(target.toString(), { signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Sections request failed: ${response.status}`);
        return response.json();
      })
    );

    const parsed = {};
    const parser = new DOMParser();
    payloads.forEach((data) => {
      Object.keys(data || {}).forEach((key) => {
        const html = data[key];
        if (typeof html === 'string' && html.trim()) {
          parsed[key] = parser.parseFromString(html, 'text/html');
        }
      });
    });
    return parsed;
  }

  /**
   * Keep "Collection - Header" in step with an in-place switch: title,
   * breadcrumb and description, taken straight from the fetched fragment so
   * they match exactly what a real load would render.
   */
  function syncCollectionHeader(doc, live) {
    const incoming = doc.getElementById(live.id);
    if (!incoming) return;

    const from = incoming.querySelector('h1');
    const to = live.querySelector('h1');
    if (from && to) to.textContent = from.textContent.trim();

    // innerHTML: the breadcrumb is a list of <li> links, not a single text node.
    const fromCrumb = incoming.querySelector('.breadcrumb');
    const toCrumb = live.querySelector('.breadcrumb');
    if (fromCrumb && toCrumb) toCrumb.innerHTML = fromCrumb.innerHTML;

    // Wraps an <expandable-container>, which re-upgrades on insertion.
    const fromDesc = incoming.querySelector('.text-content-description');
    const toDesc = live.querySelector('.text-content-description');
    if (fromDesc && toDesc) toDesc.innerHTML = fromDesc.innerHTML;
  }

  /**
   * Swap the native collection section's contents. Custom elements inside
   * re-upgrade on insertion, so facet/sort handlers rebind themselves; the
   * theme's lazy-image / tooltip / scroll-animation helpers are re-run over
   * the new markup, or the product cards stay as grey placeholders.
   * NB: `webvista` and `initializeScrollAnimationTrigger` are top-level
   * const/function bindings in other scripts — reference them bare (guarded
   * with typeof), never via `window.*`, or the calls silently no-op.
   */
  function swapNativeGrid(section, source) {
    section.innerHTML = source.innerHTML;
    if (typeof webvista !== 'undefined') {
      if (typeof webvista.initLazyImages === 'function') webvista.initLazyImages();
      if (typeof webvista.initTooltips === 'function') webvista.initTooltips();
    }
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(section);
    }
  }

  /**
   * Tell Ascent's facets popstate handler that this query is already on
   * screen, so it early-returns instead of re-rendering a (possibly hidden)
   * native grid on top of ours. `FacetFiltersForm` is a top-level class
   * binding, hence the bare typeof.
   */
  function syncFacetsPrev(query) {
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.searchParamsPrev = query;
    }
  }

  // -------------------------------------------------------------------------
  // PillNavigator — one per page
  // -------------------------------------------------------------------------

  class PillNavigator {
    constructor() {
      /** The collection on screen: { id, url, path, title, image, template }. */
      this.displayed = null;
      /** Bumped per navigation; a response whose token is stale is dropped. */
      this.token = 0;
      this.abortController = null;
      /**
       * True between committing a switch (URL + `displayed` point at the
       * target) and painting its rows. The rows on screen still belong to the
       * collection being left, so nothing may act on them in that window.
       */
      this.unsettled = false;
      /** cacheKey -> { at, pending } of section fetches (hover + in-flight). */
      this.cache = new Map();
      window.addEventListener('popstate', (event) => this.onPopState(event));
      this.initPageTransitions();
    }

    get rows() {
      return Array.from(document.querySelectorAll('pill-collection'));
    }

    /**
     * Every row calls this on connect. The first row carrying a collection
     * identity seeds what is on screen and stamps the landing history entry
     * (which starts as `null`, so Back to it had nothing to go on). A fresh
     * row swapped in after an in-place switch completes the provisional
     * title/image with the server-rendered values.
     */
    register(row) {
      const info = row.collectionInfo;
      if (!info) return;
      this.remember(info);

      if (!this.displayed) {
        // `?view=<suffix>` renders an alternate template, so the page on
        // screen is NOT the collection's assigned template and there is no
        // trustworthy baseline: mark it unknown, which makes every switch a
        // real load (see switchCollection / verify).
        if (new URLSearchParams(window.location.search).has('view')) info.template = null;
        this.displayed = info;
        this.stampEntry(info);
        this.applyAnchor();
      } else if (this.displayed.id === info.id) {
        this.displayed = Object.assign({}, this.displayed, info);
      }
    }

    // ---- collection identity ------------------------------------------------

    entryOf(info) {
      return { id: info.id, url: info.url, template: info.template };
    }

    /** Record this collection's identity on the current history entry, keeping any foreign state. */
    stampEntry(info) {
      const state = currentState();
      if (state.peCollection && state.peCollection.id === info.id) return;
      try {
        window.history.replaceState(Object.assign({}, state, { peCollection: this.entryOf(info) }), '');
      } catch (error) {
        // Non-fatal: remember() below still resolves this path on Back/Forward.
      }
    }

    /** path -> identity, so Back/Forward survives other scripts overwriting history state. */
    remember(info) {
      if (!info.path) return;
      const index = readJSON(INDEX_KEY) || {};
      const key = info.path.toLowerCase();
      delete index[key]; // re-insert as newest
      index[key] = {
        id: info.id,
        url: info.url,
        title: info.title,
        image: info.image,
        template: info.template,
      };
      const keys = Object.keys(index);
      keys.slice(0, Math.max(0, keys.length - INDEX_LIMIT)).forEach((stale) => delete index[stale]);
      writeJSON(INDEX_KEY, index);
    }

    /** The collection a restored history entry points at. */
    lookup(path) {
      const entry = currentState().peCollection;
      if (entry && entry.id && samePath(collectionPath(entry.url), path)) return entry;
      const index = readJSON(INDEX_KEY) || {};
      return index[path.toLowerCase()] || null;
    }

    // ---- trail ----------------------------------------------------------------

    /**
     * Breadcrumb of collections the shopper arrived through, newest last.
     *
     * Held in `history.state` so Back/Forward restore the right trail for
     * free, MIRRORED into sessionStorage because we are not the only writer of
     * history state: the theme's facet form and the RCC app both pushState
     * when a filter is applied, and their state has no `peTrail`. Prefer this
     * entry's copy, fall back to the stored one, and re-assert it onto the
     * entry so the repair sticks.
     */
    get trail() {
      const state = currentState();
      if (Array.isArray(state.peTrail)) return state.peTrail;

      const stored = this.readStoredTrail();
      if (stored.length) this.persistTrail(stored, this.displayed ? this.displayed.id : '', true);
      return stored;
    }

    /**
     * The stored trail, but only if it still applies to the collection on
     * screen: sessionStorage outlives a full navigation, so a shopper who left
     * via the main nav would otherwise arrive elsewhere carrying a Back pill.
     */
    readStoredTrail() {
      const data = readJSON(TRAIL_KEY);
      if (!data || !Array.isArray(data.trail)) return [];
      const current = this.displayed;
      if (current && data.at && String(data.at) !== current.id) return [];
      return data.trail;
    }

    /** Mirror the trail to sessionStorage; `repair` also writes it onto the current entry. */
    persistTrail(trail, at, repair) {
      writeJSON(TRAIL_KEY, { trail: trail, at: at || '' });
      if (!repair) return;
      try {
        const state = Object.assign({}, currentState(), { peTrail: trail, peTrailAt: at || '' });
        window.history.replaceState(state, '');
      } catch (error) {
        // Non-fatal: the trail still lives in sessionStorage.
      }
    }

    /**
     * The trail for the collection we are about to open. Landing on a
     * collection ALREADY in the trail truncates it (so returning to the start
     * empties it and the Back pill disappears); otherwise the collection being
     * left is pushed on, so A -> B -> C offers Back to B, then A, then none.
     */
    nextTrail(targetId) {
      const trail = this.trail;
      const at = trail.findIndex((entry) => String(entry.id) === String(targetId));
      if (at !== -1) return trail.slice(0, at);

      const leaving = this.displayed;
      if (!leaving) return trail;
      return trail.concat([
        {
          id: leaving.id,
          url: leaving.url,
          title: leaving.title,
          image: leaving.image,
          template: leaving.template,
        },
      ]);
    }

    /**
     * History state for a navigation. `target` is the collection being opened
     * for a collection switch, or null for a filter step (same collection, so
     * the trail carries through unchanged).
     */
    buildState(target, query) {
      const trail = target ? this.nextTrail(target.id) : this.trail;
      const landing = target || this.displayed;
      const at = landing ? landing.id : '';
      this.persistTrail(trail, at);
      return {
        pePillCollection: true,
        searchParams: query || '',
        peTrail: trail,
        peTrailAt: at,
        peCollection: landing ? this.entryOf(landing) : null,
      };
    }

    // ---- requests ---------------------------------------------------------------

    /** Start a navigation: supersede and abort whatever was in flight. */
    begin() {
      this.token += 1;
      if (this.abortController) this.abortController.abort();
      this.abortController = typeof AbortController === 'function' ? new AbortController() : null;
      return { token: this.token, signal: this.abortController ? this.abortController.signal : undefined };
    }

    isCurrent(token) {
      return token === this.token;
    }

    /** Section keys to re-render on a collection switch: every pill row, the header, Custom Liquid (+ native grid). */
    sectionKeys(mode) {
      const wrappers = this.rows.map((row) => row.closest('.shopify-section'));
      wrappers.push(findCollectionHeader(document));
      customLiquidSections().forEach((el) => wrappers.push(el));
      if (mode === 'native') wrappers.push(nativeGrid());

      const keys = [];
      wrappers.forEach((el) => {
        const key = sectionKey(el);
        if (key && keys.indexOf(key) === -1) keys.push(key);
      });
      return keys;
    }

    /**
     * Section fetch through a small TTL cache, so a hover-prefetch is reused by
     * the click that follows it. A failed or aborted fetch evicts itself.
     */
    loadSections(url, keys, signal) {
      const cacheKey = `${url}|${keys.join(',')}`;
      const hit = this.cache.get(cacheKey);
      if (hit && Date.now() - hit.at < PREFETCH_TTL_MS) return hit.pending;

      const pending = fetchSections(url, keys, signal).catch((error) => {
        if (this.cache.get(cacheKey) === entry) this.cache.delete(cacheKey);
        throw error;
      });
      const entry = { at: Date.now(), pending: pending };
      this.cache.delete(cacheKey);
      this.cache.set(cacheKey, entry);
      while (this.cache.size > PREFETCH_LIMIT) {
        this.cache.delete(this.cache.keys().next().value);
      }
      return pending;
    }

    /** Warm the sections a click on this collection card would need. */
    prefetch(url) {
      if (!this.displayed) return;
      const keys = this.sectionKeys(gridMode());
      if (keys.length) this.loadSections(url, keys).catch(() => {});
    }

    /**
     * Is this response exactly what a real load of collection `id` would
     * render? Every pill row on the page must come back as that collection,
     * on the template currently on screen — a pill host is now always
     * rendered, so a missing one is itself a mismatch — and the native grid,
     * when we drive it, must be present.
     */
    verify(fragments, id, template, mode) {
      if (!fragments) return false;
      const rows = this.rows;
      if (!rows.length) return false;

      const rowsOk = rows.every((row) => {
        const doc = fragments[sectionKey(row.closest('.shopify-section'))];
        const host = doc && doc.querySelector('pill-collection');
        return !!host && host.dataset.collectionId === String(id) && host.dataset.templateSuffix === template;
      });
      if (!rowsOk) return false;

      if (mode === 'native') {
        const grid = nativeGrid();
        const doc = grid && fragments[sectionKey(grid)];
        if (!doc || !doc.getElementById(grid.id)) return false;
      }
      return true;
    }

    /** Paint a verified response. */
    apply(fragments, mode) {
      const header = findCollectionHeader(document);
      const headerDoc = header && fragments[sectionKey(header)];
      if (headerDoc) syncCollectionHeader(headerDoc, header);

      // innerHTML, not replaceWith: same template means same section id, and
      // keeping the wrapper node keeps the theme editor's binding intact.
      // <script> tags in innerHTML do not run, which is what we want — this
      // asset is already loaded.
      customLiquidSections().forEach((live) => {
        const doc = fragments[sectionKey(live)];
        const incoming = doc && doc.getElementById(live.id);
        if (incoming && incoming.innerHTML !== live.innerHTML) live.innerHTML = incoming.innerHTML;
      });

      if (mode === 'native') {
        const grid = nativeGrid();
        swapNativeGrid(grid, fragments[sectionKey(grid)].getElementById(grid.id));
      }

      // Rows last. The fresh <pill-collection> upgrades synchronously on
      // insertion and its connectedCallback re-registers, injects the Back
      // pill and syncs the highlight against the (already updated) URL.
      this.rows
        .map((row) => row.closest('.shopify-section'))
        .forEach((live) => {
          const doc = live && fragments[sectionKey(live)];
          const incoming = doc && doc.getElementById(live.id);
          if (incoming) live.innerHTML = incoming.innerHTML;
        });
    }

    setLoading(state, grid) {
      this.rows.forEach((row) => row.classList.toggle('pe-pill-collection--loading', state));
      if (!grid) return;

      // Native grid: reuse the theme's own `.loading` class on the list.
      const list = grid.querySelector('#Product-Grid-Container .content-list');
      if (list) list.classList.toggle('loading', state);
      const container = grid.querySelector('#Product-Grid-Container');
      if (container) container.setAttribute('aria-busy', state ? 'true' : 'false');

      // The Vertex app block lives outside this section, so scoped CSS can't
      // reach it — dim it inline instead.
      if (grid.id && grid.id.indexOf('retail_cloud_connect') !== -1) {
        grid.style.transition = 'opacity 0.15s ease';
        grid.style.opacity = state ? '0.5' : '';
        grid.style.pointerEvents = state ? 'none' : '';
        grid.setAttribute('aria-busy', state ? 'true' : 'false');
      }
    }

    /**
     * We can't know when the RCC app finishes, so clear the dim on its first
     * re-render, with a timeout backstop. Attach BEFORE triggering the app: its
     * handlers can run synchronously, and a later attach could miss the
     * mutation and hold the dim until the timeout.
     */
    clearLoadingOnRerender(grid) {
      let done = false;
      let observer = null;
      let timer = 0;
      const finish = () => {
        if (done) return;
        done = true;
        if (observer) observer.disconnect();
        clearTimeout(timer);
        this.setLoading(false, grid);
      };
      if ('MutationObserver' in window) {
        observer = new MutationObserver(finish);
        observer.observe(grid, { childList: true, subtree: true });
      }
      timer = setTimeout(finish, 3000);
    }

    syncRows() {
      this.rows.forEach((row) => {
        row.applyBackPill();
        row.syncActiveFromUrl();
      });
    }

    // ---- full-load smoothing ---------------------------------------------------

    /**
     * A pill whose target is on another template has to load the page, and
     * the new page needs ~1s before it can paint (a 1.5MB document behind a
     * render-blocking <head>). That wait cannot be animated away — a
     * cross-document View Transition was tried and made it WORSE: the old page
     * freezes, then a fade adds 200ms on top. So instead:
     *   - react to the click on the very next frame (pill marked, row and grid
     *     dimmed) — Chrome keeps that frame on screen until the new page
     *     paints, so the shopper sees the click was taken;
     *   - shorten the wait: cross-template cards are prefetched through the
     *     Speculation Rules API on hover / pointerdown (see initPrefetchRules);
     *   - land the new page with its pill row where the clicked row was,
     *     instead of the jump to the top a fresh load does.
     */
    initPageTransitions() {
      this.initPrefetchRules();

      // Arriving: pick up an anchor the previous page left for us. Only on a
      // plain navigation — a reload or Back/Forward has its own scroll to keep.
      const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      const anchor = readJSON(ANCHOR_KEY);
      if (anchor) {
        try {
          sessionStorage.removeItem(ANCHOR_KEY);
        } catch (error) {
          // Unreadable storage returned null above; nothing to clear.
        }
      }
      const fresh = anchor && typeof anchor.top === 'number' && Date.now() - anchor.at < ANCHOR_TTL_MS;
      this.anchor = fresh && (!nav || nav.type === 'navigate') ? anchor : null;
      if (!this.anchor) return;

      // Re-apply as the layout above the row settles (fonts, images, app
      // blocks). pagereveal runs right before the first frame, so where it
      // fires after this script the page is first painted already in position.
      const settle = () => this.applyAnchor();
      window.addEventListener('pagereveal', settle, { once: true });
      document.addEventListener('DOMContentLoaded', settle, { once: true });
      window.addEventListener(
        'load',
        () => {
          settle();
          this.anchor = null;
        },
        { once: true }
      );
    }

    /**
     * Speculation Rules prefetch for pill cards that will do a real load
     * (marked `data-pill-prefetch` by the rows). PREFETCH, not prerender: it
     * fetches the document only and runs none of its scripts, so analytics,
     * pixels and the RCC search never fire for a page the shopper does not
     * open. `moderate` = after ~200ms of hover on desktop, on pointerdown on
     * touch. Browsers without support ignore the rule.
     */
    initPrefetchRules() {
      if (!(HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'))) return;
      const rules = document.createElement('script');
      rules.type = 'speculationrules';
      rules.textContent = JSON.stringify({
        prefetch: [{ where: { selector_matches: 'pill-collection a[data-pill-prefetch]' }, eagerness: 'moderate' }],
      });
      document.head.appendChild(rules);
    }

    /** Leaving by a pill: show the click was taken and tell the next page where the row sat. */
    markRealLoad(id, row) {
      this.setLoading(true, gridMode() === 'vertex' ? vertexGrid() : nativeGrid());
      if (!row || !row.isConnected) return;
      writeJSON(ANCHOR_KEY, {
        id: String(id),
        top: Math.max(0, row.getBoundingClientRect().top),
        at: Date.now(),
      });
    }

    /**
     * Scroll so the first visible pill row sits at the recorded viewport
     * offset. Stops for good once the shopper scrolls themselves, and only
     * applies to the collection the anchor was recorded for.
     */
    applyAnchor() {
      const anchor = this.anchor;
      if (!anchor || !this.displayed || this.displayed.id !== anchor.id) return;

      if (typeof this.anchorScrollY === 'number' && Math.abs(window.scrollY - this.anchorScrollY) > 2) {
        this.anchor = null; // the shopper has taken over
        return;
      }

      const row = this.rows.find((el) => {
        const root = el.closest('.pe-pill-collection');
        return root && !root.hidden;
      });
      if (!row) return;

      const delta = row.getBoundingClientRect().top - anchor.top;
      if (Math.abs(delta) > 1) window.scrollTo(0, Math.max(0, window.scrollY + delta));
      this.anchorScrollY = window.scrollY;
    }

    // ---- navigations ------------------------------------------------------------

    /**
     * Switch to another collection. `push` is false when replaying a history
     * entry on Back/Forward, where the URL has already changed.
     */
    async switchCollection({ url, id, template, title, image, push, row }) {
      const current = this.displayed;
      const mode = gridMode();
      const realLoad = () => {
        if (!push) {
          window.location.reload();
          return;
        }
        // A real load leaves history.state behind, so hand the target page its
        // trail through sessionStorage (readStoredTrail accepts it because it
        // is tagged with the target's id) — or it arrives with no Back pill.
        if (id) this.persistTrail(this.nextTrail(id), String(id));
        this.markRealLoad(id, row);
        // Navigate from the next frame so the click feedback markRealLoad just
        // applied is guaranteed to paint first — with a prefetched document
        // the old page can otherwise be swapped out before it ever shows.
        // rAF never fires while the page is hidden (tab switched right after
        // the tap, throttling webviews), so a timer guarantees we still go.
        let gone = false;
        const go = () => {
          if (gone) return;
          gone = true;
          window.location.assign(url);
        };
        window.requestAnimationFrame(go);
        setTimeout(go, 50);
      };

      if (!current || !id || mode === 'none') return realLoad();
      // Known to be on another template: only a real load renders it right.
      if (template !== null && template !== current.template) return realLoad();

      const search = mode === 'vertex' ? vertexSearch() : null;
      if (mode === 'vertex' && (!search || typeof search.setCollection !== 'function')) return realLoad();

      const { token, signal } = this.begin();
      const pending = this.loadSections(url, this.sectionKeys(mode), signal).catch(() => null);
      const grid = mode === 'vertex' ? vertexGrid() : nativeGrid();
      this.setLoading(true, grid);

      // Template unknown (e.g. a Back pill from a trail recorded before this
      // code shipped): verify BEFORE touching the grid or history, so a
      // mismatch costs nothing but a normal navigation.
      if (template === null) {
        const early = await pending;
        if (!this.isCurrent(token)) return;
        if (!this.verify(early, id, current.template, mode)) {
          this.setLoading(false, grid);
          return realLoad();
        }
      }

      const target = {
        id: String(id),
        url: new URL(url, window.location.origin).pathname,
        path: collectionPath(url),
        title: title || '',
        image: image || '',
        template: current.template,
      };
      const query = queryOf(url);

      if (push) window.history.pushState(this.buildState(target, query), '', url);
      this.displayed = target;
      this.unsettled = true;
      this.remember(target);
      syncFacetsPrev(query);

      // Start the product query now rather than after the pill fragments land.
      if (mode === 'vertex') {
        this.clearLoadingOnRerender(grid);
        try {
          // A replayed entry keeps whatever filters the app restores from its
          // URL; a fresh switch starts the target unfiltered.
          search.setCollection(target.id, { clearFilters: push });
        } catch (error) {
          window.location.reload();
          return;
        }
      }

      const fragments = await pending;
      if (!this.isCurrent(token)) return;

      // The URL and grid already point at the target, so a failed or
      // mismatched response reloads THIS url — never paint a hybrid.
      if (!this.verify(fragments, target.id, target.template, mode)) {
        window.location.reload();
        return;
      }

      this.apply(fragments, mode);
      this.unsettled = false;
      if (mode === 'native') this.setLoading(false, grid);
    }

    /** Filter the collection on screen (a filter card). */
    async filter(url) {
      const mode = gridMode();
      // A filter card clicked mid-switch belongs to the row being replaced;
      // resolving it in place would filter the wrong collection.
      if (mode === 'none' || !this.displayed || this.unsettled) {
        window.location.assign(url);
        return;
      }

      const { token, signal } = this.begin();
      const query = queryOf(url);

      if (mode === 'vertex') {
        const grid = vertexGrid();
        this.setLoading(true, grid);
        window.history.pushState(this.buildState(null, query), '', url);
        syncFacetsPrev(query);
        this.clearLoadingOnRerender(grid);
        // Mirror the Back button: let the app re-read window.location and
        // re-search. Flagged so our own popstate handler ignores it.
        const event = new PopStateEvent('popstate', { state: window.history.state });
        event.pePillSynthetic = true;
        window.dispatchEvent(event);
        this.syncRows();
        return;
      }

      // Native: fetch just the grid fragment for the filtered URL. Not cached —
      // a filter result is cheap to re-ask for and staleness would show.
      const grid = nativeGrid();
      const key = sectionKey(grid);
      this.setLoading(true, grid);
      try {
        const fragments = await fetchSections(url, [key], signal);
        if (!this.isCurrent(token)) return;
        const source = fragments[key] && fragments[key].getElementById(grid.id);
        if (!source || !source.innerHTML.trim()) throw new Error('Collection section not found in response');

        swapNativeGrid(grid, source);
        window.history.pushState(this.buildState(null, query), '', url);
        syncFacetsPrev(query);
        this.syncRows();
      } catch (error) {
        if (!this.isCurrent(token)) return; // superseded (includes our own abort)
        window.location.assign(url); // a real navigation always works
      } finally {
        if (this.isCurrent(token)) this.setLoading(false, grid);
      }
    }

    /**
     * Back/Forward. One handler for the page, however many rows it has.
     *   - same collection  -> a filter step; the RCC app / theme facets
     *                         re-render the grid themselves, we reconcile
     *                         the highlight and Back pill.
     *   - other collection -> replay the switch in place when we know the
     *                         collection and it shares the template,
     *                         otherwise reload. The RCC app re-reads filters
     *                         on popstate but never the collection, so doing
     *                         nothing left the previous collection's
     *                         products under the restored URL.
     */
    onPopState(event) {
      if (event.pePillSynthetic) return;
      const current = this.displayed;
      const path = collectionPath(window.location.href);
      if (!current || !path) return;

      if (samePath(path, current.path)) {
        // Rows still belong to a collection we were leaving: only a real load
        // can reconcile them with this entry.
        if (this.unsettled) {
          window.location.reload();
          return;
        }
        // A newer history step supersedes anything still in flight — and
        // whatever it dimmed.
        this.begin();
        this.setLoading(false, nativeGrid());
        this.syncRows();
        return;
      }

      const entry = this.lookup(path);
      if (!entry || !entry.id) {
        window.location.reload();
        return;
      }

      this.switchCollection({
        url: window.location.href,
        id: String(entry.id),
        template: templateOf(entry.template),
        title: entry.title,
        image: entry.image,
        push: false,
      });
    }
  }

  const pillNav = new PillNavigator();

  // -------------------------------------------------------------------------
  // <pill-collection> — one per row
  // -------------------------------------------------------------------------

  class PillCollection extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onPointerOver = this.onPointerOver.bind(this);
      this.onPointerOut = this.onPointerOut.bind(this);
      this.hoverTimer = 0;
      this.hoverCard = null;
    }

    connectedCallback() {
      pillNav.register(this);
      this.addEventListener('click', this.onClick);
      // pointerover/out bubble (enter/leave do not), so one pair covers all cards.
      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('pointerout', this.onPointerOut);
      // history.state survives a reload, so a shopper who reloads mid-journey
      // still gets their Back pill.
      this.applyBackPill();
      this.syncActiveFromUrl();
      this.initDrag();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerover', this.onPointerOver);
      this.removeEventListener('pointerout', this.onPointerOut);
      clearTimeout(this.hoverTimer);
      if (this.track) {
        this.track.removeEventListener('mousedown', this.onTrackMouseDown);
        this.track.removeEventListener('dragstart', this.onTrackDragStart);
      }
      document.removeEventListener('mousemove', this.onTrackMouseMove);
      document.removeEventListener('mouseup', this.onTrackMouseUp);
    }

    /** The collection this row was server-rendered FOR, as baked in by Liquid. */
    get collectionInfo() {
      const data = this.dataset;
      if (!data.collectionId) return null;
      const url = data.collectionUrl || '';
      return {
        id: String(data.collectionId),
        url: url,
        path: collectionPath(url),
        title: data.collectionTitle || '',
        image: data.collectionImage || '',
        template: templateOf(data.templateSuffix),
      };
    }

    /**
     * Re-read the card list. Must be called after injecting the Back pill so
     * setActive/syncActiveFromUrl treat it like any other card.
     */
    refreshCards() {
      this.cards = Array.from(this.querySelectorAll('[data-pill-card]'));
    }

    /**
     * The section renders `hidden` when it has no cards; show it exactly when
     * the row has something in it (typically a lone Back pill). Left alone in
     * the theme editor, where Liquid never hides it.
     */
    syncVisibility() {
      const root = this.closest('.pe-pill-collection');
      if (!root || isDesignMode()) return;
      root.hidden = !this.querySelector('.pe-pill-collection__item');
    }

    // ---- drag to scroll ---------------------------------------------------------

    /**
     * Desktop mouse drag-to-scroll on the card row — same "grab and drag" feel
     * as PE - Collection list. Touch devices keep native scrolling. A real drag
     * suppresses the click that follows, so dragging never navigates a card.
     */
    initDrag() {
      this.track = this.querySelector('.pe-pill-collection__track');
      if (!this.track) return;

      this.drag = { down: false, moved: false, startX: 0, startScroll: 0 };
      this.onTrackMouseDown = this.onTrackMouseDown.bind(this);
      this.onTrackMouseMove = this.onTrackMouseMove.bind(this);
      this.onTrackMouseUp = this.onTrackMouseUp.bind(this);
      // Cancel the browser's native image/link drag ghost during a scroll drag.
      this.onTrackDragStart = (event) => event.preventDefault();

      this.track.addEventListener('mousedown', this.onTrackMouseDown);
      this.track.addEventListener('dragstart', this.onTrackDragStart);
    }

    onTrackMouseDown(event) {
      if (event.button !== 0) return; // Left button only.
      this.suppressClick = false;
      this.drag.down = true;
      this.drag.moved = false;
      this.drag.startX = event.pageX;
      this.drag.startScroll = this.track.scrollLeft;
      document.addEventListener('mousemove', this.onTrackMouseMove);
      document.addEventListener('mouseup', this.onTrackMouseUp);
    }

    onTrackMouseMove(event) {
      if (!this.drag.down) return;
      const dx = event.pageX - this.drag.startX;
      if (!this.drag.moved && Math.abs(dx) < 5) return; // Small threshold before it counts as a drag.
      this.drag.moved = true;
      event.preventDefault();
      this.track.classList.add('pe-pill-collection__track--dragging');
      this.track.scrollLeft = this.drag.startScroll - dx;
    }

    onTrackMouseUp() {
      if (!this.drag.down) return;
      this.drag.down = false;
      document.removeEventListener('mousemove', this.onTrackMouseMove);
      document.removeEventListener('mouseup', this.onTrackMouseUp);
      this.track.classList.remove('pe-pill-collection__track--dragging');

      // A drag just happened: swallow the click it produces. Clear on the next
      // tick so a later genuine click is unaffected even if no click came.
      if (this.drag.moved) {
        this.suppressClick = true;
        setTimeout(() => {
          this.suppressClick = false;
        }, 0);
      }
    }

    // ---- clicks and hover -------------------------------------------------------

    onClick(event) {
      if (this.suppressClick) {
        this.suppressClick = false;
        event.preventDefault();
        return;
      }

      const card = event.target.closest('[data-pill-card]');
      if (!card || !this.contains(card)) return;

      // Let modifier / middle clicks fall through so "open in new tab" works.
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const url = this.resolveUrl(card);
      if (!url) return;
      event.preventDefault();

      if (card.dataset.pillMode !== 'collection') {
        this.setActive(card);
        pillNav.filter(url);
        return;
      }

      const id = card.dataset.pillCollectionId;
      if (!id) {
        window.location.assign(url);
        return;
      }

      this.setActive(card);
      // Title and image are provisional — the target's own row replaces them
      // when it lands — but they keep the Back pill whole if the shopper moves
      // on again before it does.
      const label = card.querySelector('.pe-pill-collection__title, .pe-pill-collection__capsule');
      const img = card.querySelector('img');
      pillNav.switchCollection({
        url: url,
        id: String(id),
        template: templateOf(card.dataset.pillTemplate),
        title: label ? label.textContent.trim() : '',
        image: img ? img.currentSrc || img.src : '',
        push: true,
        row: this,
      });
    }

    /**
     * Target URL for a card at click time. A collection card uses its baked
     * href. A FILTER card filters the collection on screen NOW, so it is
     * rebuilt against the current collection path; the baked href stays as
     * the no-JS fallback.
     */
    resolveUrl(card) {
      const href = card.getAttribute('href');
      if (card.getAttribute('data-pill-mode') !== 'filter') return href;

      const base = collectionPath(window.location.href);
      if (!base) return href; // Not on a collection page — trust the baked href.

      // Keep an alternate-template `view` param: dropping it would filter the
      // collection on its assigned template instead of the one on screen.
      const params = new URLSearchParams(card.getAttribute('data-pill-filter') || '');
      const view = new URLSearchParams(window.location.search).get('view');
      if (view && !params.has('view')) params.set('view', view);
      const query = params.toString();
      return query ? `${base}?${query}` : base;
    }

    /**
     * Prefetch a collection card's sections once the pointer has rested on it
     * briefly — a pointer sweeping across the row fires nothing. Cards known
     * to be on another template are skipped: they navigate normally.
     */
    onPointerOver(event) {
      const card = event.target.closest && event.target.closest('[data-pill-card]');
      if (!card || card === this.hoverCard || !this.contains(card)) return;
      clearTimeout(this.hoverTimer);
      this.hoverCard = card;
      if (card.dataset.pillMode !== 'collection') return;

      const template = templateOf(card.dataset.pillTemplate);
      const current = pillNav.displayed;
      if (!current || (template !== null && template !== current.template)) return;

      this.hoverTimer = setTimeout(() => {
        const url = this.resolveUrl(card);
        if (url) pillNav.prefetch(url);
      }, HOVER_DELAY_MS);
    }

    onPointerOut(event) {
      if (!this.hoverCard) return;
      if (event.relatedTarget && this.hoverCard.contains(event.relatedTarget)) return;
      clearTimeout(this.hoverTimer);
      this.hoverCard = null;
    }

    // ---- highlight and Back pill ------------------------------------------------

    /**
     * Best-effort active-pill highlight based on the current URL. A card
     * counts as active when its path matches and every query param it sets is
     * present in the current URL (so extra app params like _pos/_psid don't
     * break it). The most specific match wins.
     */
    syncActiveFromUrl() {
      if (!this.cards) this.refreshCards();
      const here = new URL(window.location.href);
      let match = null;

      this.cards.forEach((card) => {
        const href = card.getAttribute('href');
        if (!href) return;
        const target = new URL(href, window.location.origin);
        if (target.pathname !== here.pathname) return;

        let allPresent = true;
        target.searchParams.forEach((value, key) => {
          if (here.searchParams.getAll(key).indexOf(value) === -1) allPresent = false;
        });
        const size = Array.from(target.searchParams.keys()).length;
        if (allPresent && (!match || size > match.size)) match = { card, size };
      });

      this.setActive(match ? match.card : null);
    }

    setActive(card) {
      this.cards.forEach((el) => {
        const isActive = el === card;
        el.classList.toggle('is-active', isActive);
        if (isActive) {
          el.setAttribute('aria-current', 'true');
        } else {
          el.removeAttribute('aria-current');
        }
      });
    }

    /**
     * Prepend (or remove) the Back pill according to the trail. The row is
     * server-rendered from the collection's own sub-nav, which knows nothing
     * about where the shopper came from — so the Back pill is a client-side
     * injection carrying the same `data-pill-*` contract as any collection
     * card, and clicking it goes through the normal switch.
     */
    applyBackPill() {
      const track = this.querySelector('.pe-pill-collection__track');
      if (!track) return;

      // Always clear first: this also handles the trail becoming empty.
      track.querySelectorAll('[data-pill-back-item]').forEach((el) => el.remove());

      const trail = pillNav.trail;
      const previous = trail[trail.length - 1];
      if (previous && previous.id) {
        track.insertAdjacentElement('afterbegin', this.buildBackPill(previous));
      }
      this.refreshCards();
      this.markPrefetchable();
      this.syncVisibility();
    }

    /**
     * Flag the collection cards that will do a REAL load (target known to be on
     * another template) for the Speculation Rules prefetch. Same-template cards
     * switch in place and warm their section fragments instead; cards with an
     * unknown template verify first and are left alone.
     */
    markPrefetchable() {
      const current = pillNav.displayed;
      this.cards.forEach((card) => {
        const template = templateOf(card.dataset.pillTemplate);
        const realLoad =
          card.dataset.pillMode === 'collection' &&
          !!current &&
          template !== null &&
          template !== current.template;
        card.toggleAttribute('data-pill-prefetch', realLoad);
      });
    }

    /** Build the Back pill: previous collection's image, dimmed, arrow + label over it. */
    buildBackPill(previous) {
      const label = this.dataset.backLabel || 'Back';

      const item = document.createElement('li');
      item.className = 'pe-pill-collection__item';
      item.setAttribute('data-pill-back-item', '');

      const link = document.createElement('a');
      link.className = 'pe-pill-collection__card pe-pill-collection__card--back';
      link.href = previous.url || '#';
      link.setAttribute('data-pill-card', '');
      link.setAttribute('data-pill-back', '');
      link.setAttribute('data-pill-mode', 'collection');
      link.setAttribute('data-pill-filter', '');
      link.setAttribute('data-pill-collection-id', previous.id);
      // Absent (not '') for a trail recorded before templates were tracked, so
      // the switch verifies the response instead of trusting a guess.
      if (typeof previous.template === 'string') link.setAttribute('data-pill-template', previous.template);
      link.setAttribute('aria-label', previous.title ? `${label}: ${previous.title}` : label);

      // Capsule shape has no image to dim, so the Back pill is an arrow sitting
      // inline before its label, styled like any other capsule.
      if ((this.dataset.shape || 'pill') === 'pill') {
        const capsule = document.createElement('span');
        capsule.className = 'pe-pill-collection__capsule';
        capsule.innerHTML = BACK_ARROW_SVG; // static markup, no interpolated data

        const capsuleText = document.createElement('span');
        capsuleText.textContent = label;
        capsule.appendChild(capsuleText);

        link.appendChild(capsule);
        item.appendChild(link);
        return item;
      }

      const media = document.createElement('span');
      media.className = 'pe-pill-collection__media';

      if (previous.image) {
        const img = document.createElement('img');
        img.src = previous.image;
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);
      }

      const overlay = document.createElement('span');
      overlay.className = 'pe-pill-collection__back-overlay';
      overlay.innerHTML = BACK_ARROW_SVG; // static markup, no interpolated data

      const text = document.createElement('span');
      text.className = 'pe-pill-collection__back-label';
      text.textContent = label; // textContent, so a merchant label can't inject markup
      overlay.appendChild(text);

      media.appendChild(overlay);
      link.appendChild(media);
      item.appendChild(link);
      return item;
    }
  }

  customElements.define('pill-collection', PillCollection);
})();
